import { useEffect, useState, useMemo, MouseEvent } from "react";
import { usePageTitle } from "../../../../hooks/usePageTitle";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import {
  Stack,
  Typography,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { red } from "@mui/material/colors";
import { ExplorerAPI } from "../../../../util/Requests";
import { ExplorerEnvironment } from "../../../../data/Cache";
import { NBAGameListItem } from "../../../../util/types";
import { compareByGameDate } from "./utils";
import { RosterPlayer, PlayerRotationMinutes } from "./types";
import {
  NBA_LEAGUE_ID,
  PORTLAND_TEAM_ID,
  QUARTERS,
  PLAYER_COLORS,
  PLAYER_SLOTS,
} from "./constants";
import { loadCurrentSeason } from "../../../../util/season";
import GameHeader from "./GameHeader";
import QuarterGrid from "./QuarterGrid";
import { getCellKey, getInitials } from "./QuarterGrid/utils";
import Summary from "./Summary";
import { countMinutes, canCarryOver, hasAnyFilled } from "./utils";
import { useRotationPlan } from "./hooks";

export default function Rotations() {
  const theme = useTheme();
  const [roster, setRoster] = useState<RosterPlayer[]>([]);
  const [games, setGames] = useState<NBAGameListItem[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedCell, setSelectedCell] = useState<{
    quarter: string;
    minute: number;
    slot: number;
  } | null>(null);

  const {
    slotAssignments,
    excludedPlayerIds,
    loadingPlan,
    saving,
    saveSuccess,
    hasUnsavedChanges,
    assignPlayer,
    clearSlot,
    carryOver,
    clearQuarter,
    excludePlayer,
    includePlayer,
    save,
  } = useRotationPlan(selectedGameId);

  useEffect(() => {
    const api = new ExplorerAPI(ExplorerEnvironment.NBA);
    api
      .getTeamRoster(PORTLAND_TEAM_ID, NBA_LEAGUE_ID)
      .then(({ players }: { players: RosterPlayer[] }) => {
        setRoster(players);
      })
      .catch((error) => {
        console.error("Failed to fetch roster:", error);
      })
      .finally(() => {
        setLoading(false);
      });

    // seasonAny: a game list turns over at the first game of any type, so the
    // preseason's games are plannable.
    loadCurrentSeason(api)
      .then(({ seasonAny }) => api.getTeamGames(PORTLAND_TEAM_ID, seasonAny))
      .then((gamesList) => {
        setGames(gamesList);
      })
      .catch((error) => {
        console.error("Failed to fetch games:", error);
      });
  }, []);

  const { todayGame, pastGames, upcomingGames } = useMemo((): {
    todayGame: NBAGameListItem | null;
    pastGames: NBAGameListItem[];
    upcomingGames: NBAGameListItem[];
  } => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let todayGame: NBAGameListItem | null = null;
    const past: NBAGameListItem[] = [];
    const upcoming: NBAGameListItem[] = [];

    games.forEach((game) => {
      const gameDate = new Date(game.gameDateTimeUTC);
      gameDate.setHours(0, 0, 0, 0);
      if (gameDate.getTime() === today.getTime()) {
        todayGame = game;
      } else if (gameDate < today) {
        past.push(game);
      } else {
        upcoming.push(game);
      }
    });

    return {
      todayGame,
      pastGames: past.toSorted((a, b) => compareByGameDate(b, a)),
      upcomingGames: upcoming.toSorted(compareByGameDate),
    };
  }, [games]);

  useEffect(() => {
    if (selectedGameId) return;
    if (todayGame) {
      setSelectedGameId(todayGame.gameId);
    } else if (upcomingGames.length > 0) {
      setSelectedGameId(upcomingGames[0].gameId);
    } else if (pastGames.length > 0) {
      setSelectedGameId(pastGames[0].gameId);
    }
  }, [todayGame, upcomingGames, pastGames, selectedGameId]);

  const selectedGame = useMemo(
    (): NBAGameListItem | undefined =>
      games.find((g) => g.gameId === selectedGameId),
    [games, selectedGameId],
  );

  const rotationsTitle = useMemo((): string => {
    if (!selectedGame) return "Rotations - Explorer";
    const opponent =
      selectedGame.homeTeam.teamId === PORTLAND_TEAM_ID
        ? selectedGame.awayTeam
        : selectedGame.homeTeam;
    const isHome = selectedGame.homeTeam.teamId === PORTLAND_TEAM_ID;
    const dateStr = dayjs(selectedGame.gameDateEst.split("T")[0]).format("M/D");
    return `${dateStr} ${isHome ? "vs" : "at"} ${opponent.teamTricode} Rotations - Explorer`;
  }, [selectedGame]);

  usePageTitle(rotationsTitle);

  const gameNumber = useMemo((): number => {
    const regularSeason = games
      .filter((g) => g.seasonType === "Regular Season")
      .toSorted(compareByGameDate);
    return regularSeason.findIndex((g) => g.gameId === selectedGameId) + 1;
  }, [games, selectedGameId]);

  const handleGameChange = (event: SelectChangeEvent<string>) => {
    setSelectedGameId(event.target.value);
  };

  const playerColorMap = useMemo(
    (): Record<number, string> =>
      roster.reduce<Record<number, string>>(
        (acc, { playerId }, index) => ({
          ...acc,
          [playerId]: PLAYER_COLORS[index % PLAYER_COLORS.length],
        }),
        {},
      ),
    [roster],
  );

  const playerMinutes = useMemo(
    (): PlayerRotationMinutes[] =>
      roster
        .map(({ playerId, playerName, position }) => ({
          playerId,
          playerName,
          position,
          q1Minutes: countMinutes(playerId, "Q1", slotAssignments),
          q2Minutes: countMinutes(playerId, "Q2", slotAssignments),
          q3Minutes: countMinutes(playerId, "Q3", slotAssignments),
          q4Minutes: countMinutes(playerId, "Q4", slotAssignments),
        }))
        .toSorted((a, b) => {
          const aExcluded = excludedPlayerIds.has(a.playerId);
          const bExcluded = excludedPlayerIds.has(b.playerId);
          if (aExcluded !== bExcluded) return aExcluded ? 1 : -1;
          const totalA = a.q1Minutes + a.q2Minutes + a.q3Minutes + a.q4Minutes;
          const totalB = b.q1Minutes + b.q2Minutes + b.q3Minutes + b.q4Minutes;
          if (totalA !== totalB) return totalB - totalA;
          return a.playerName.localeCompare(b.playerName);
        }),
    [roster, slotAssignments, excludedPlayerIds],
  );

  const sortedRoster = useMemo(
    (): RosterPlayer[] =>
      roster
        .filter((player) => !excludedPlayerIds.has(player.playerId))
        .toSorted((a, b) => a.playerName.localeCompare(b.playerName)),
    [roster, excludedPlayerIds],
  );

  const handleClearSlot = () => {
    if (!selectedCell) return;
    clearSlot(selectedCell.quarter, selectedCell.minute, selectedCell.slot);
    handleClosePopover();
  };

  const handleCellClick = (
    event: MouseEvent<HTMLElement>,
    quarter: string,
    minute: number,
    slot: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedCell({ quarter, minute, slot });
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedCell(null);
  };

  const handlePlayerSelect = (playerId: number) => {
    if (!selectedCell) return;
    assignPlayer(
      selectedCell.quarter,
      selectedCell.minute,
      selectedCell.slot,
      playerId,
    );
    handleClosePopover();
  };

  const renderGameMenuItem = (game: NBAGameListItem) => {
    const opponent =
      game.homeTeam.teamId === PORTLAND_TEAM_ID ? game.awayTeam : game.homeTeam;
    const isHome = game.homeTeam.teamId === PORTLAND_TEAM_ID;
    const dateStr = dayjs(game.gameDateEst.split("T")[0]).format("M/D/YYYY");
    return (
      <MenuItem key={game.gameId} value={game.gameId}>
        {dateStr} {isHome ? "vs" : "@"} {opponent.teamTricode}
      </MenuItem>
    );
  };

  return (
    <Stack
      sx={{
        p: { xs: 2, md: 4 },
        alignItems: "center",
        position: "relative",
        "@media print": { p: 0, alignItems: "flex-start" },
      }}
    >
      <style>{`@page { size: portrait; margin: 0.3in; }`}</style>
      {(loading || loadingPlan || saving) && (
        <Stack
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <CircularProgress />
        </Stack>
      )}
      <Stack sx={{ width: "fit-content" }}>
        {selectedGame && (
          <GameHeader game={selectedGame} gameNumber={gameNumber} />
        )}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            mb: 4,
            alignItems: "center",
            "@media print": { display: "none" },
          }}
        >
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Game</InputLabel>
            <Select
              value={selectedGameId}
              label="Game"
              onChange={handleGameChange}
            >
              {upcomingGames.slice(0, 5).toReversed().map(renderGameMenuItem)}
              {todayGame && renderGameMenuItem(todayGame)}
              {pastGames.map(renderGameMenuItem)}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color={saveSuccess ? "success" : "secondary"}
            onClick={() => selectedGame && save(selectedGame)}
            disabled={saving || !selectedGameId || !hasUnsavedChanges}
            sx={{ minWidth: 100 }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : saveSuccess ? (
              "Saved"
            ) : (
              "Save"
            )}
          </Button>
        </Stack>
        <Stack
          direction="column"
          gap={4}
          sx={{
            "@media (min-width: 1100px)": { flexDirection: "row" },
            "@media print": { flexDirection: "row", gap: 2 },
          }}
        >
          <Stack spacing={2}>
            {QUARTERS.map((quarter) => (
              <QuarterGrid
                key={quarter}
                quarter={quarter}
                slotAssignments={slotAssignments}
                roster={roster}
                playerColorMap={playerColorMap}
                onCellClick={handleCellClick}
                onCarryOver={
                  canCarryOver(quarter, slotAssignments)
                    ? () => carryOver(quarter)
                    : undefined
                }
                onClear={
                  hasAnyFilled(quarter, slotAssignments)
                    ? () => clearQuarter(quarter)
                    : undefined
                }
              />
            ))}
          </Stack>
          <Summary
            players={playerMinutes}
            loading={loading}
            excludedPlayerIds={excludedPlayerIds}
            playerColorMap={playerColorMap}
            onExclude={excludePlayer}
            onInclude={includePlayer}
          />
        </Stack>
      </Stack>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <List
          dense
          disablePadding
          sx={{ maxHeight: 500, overflow: "auto", minWidth: 180 }}
        >
          {selectedCell &&
            slotAssignments[
              getCellKey(
                selectedCell.quarter,
                selectedCell.minute,
                selectedCell.slot,
              )
            ] && (
              <ListItemButton onClick={handleClearSlot}>
                <ListItemText
                  primary="Clear"
                  primaryTypographyProps={{ color: red[400] }}
                />
              </ListItemButton>
            )}
          {sortedRoster.map((player) => {
            const isAlreadyAssigned =
              selectedCell &&
              PLAYER_SLOTS.some(
                (slot) =>
                  slotAssignments[
                    getCellKey(selectedCell.quarter, selectedCell.minute, slot)
                  ] === player.playerId,
              );
            const color = playerColorMap[player.playerId];
            return (
              <ListItemButton
                key={player.playerId}
                onClick={() => handlePlayerSelect(player.playerId)}
                disabled={!!isAlreadyAssigned}
                sx={{ px: 1 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 0.5,
                      bgcolor: color,
                      color: theme.palette.getContrastText(color),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: "bold",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(player.playerName)}
                  </Typography>
                  <Typography variant="body2">{player.playerName}</Typography>
                </Stack>
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </Stack>
  );
}
